import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { lckServices } from "../services/lck-api";
import {
  LckAclSet,
  LckChapter,
  LckPage,
  LckTableRow,
  type LckTableRowData,
  LckTableRowDataComplex,
  LckTableView,
  type LckTableRowOffline,
  type LckTableViewColumn,
  type LckGroup,
} from "../services/lck-api/definitions";

import { useStoreApp } from "./app";
import type { Paginated } from "@feathersjs/feathers";
import {
  BLOCK_TYPE,
  type BlockFormRecord,
  type BlockDataRecord,
  type BlockTableSet,
  COLUMN_TYPE,
} from "@locokit/lck-glossary";
import { formatRowData } from "@/services/lck-api/helpers";
import { normalizeString } from "@/services/normalize";
import { dexieStorageInstance } from "@/services/storage/dexie";

enum SYNC_STATUS {
  WAITING = "WAITING",
  IN_PROGRESS = "IN_PROGRESS",
  FAILURE = "FAILURE",
  SUCCESS = "SUCCESS",
}

enum SYNC_ACTION {
  POST = "POST",
  PATCH = "PATCH",
  DELETE = "DELETE",
}

/**
 * Store all data needed for offline use
 *
 * Actually, is needed :
 * * diving sites
 * * boats
 * * ?
 *
 * Store also data created during offline use :
 * * dives
 * * ?
 *
 */
export const useStoreData = defineStore("data", () => {
  const dataState = ref({
    loading: false,
    error: null as Error | null,
    offlineReady: false,
    groups: [] as LckGroup[],
    /**
     * Views available for the current logged user,
     * stored by groupIds
     */
    views: {} as Record<string, LckTableView[]>,
    /**
     * Offline data dictionary per groupIds / tableIds,
     * store all elements for RBT fields
     */
    offlineDataDictionary: {} as Record<string, Record<string, LckTableRow[]>>,
    offlineDataServer: {} as Record<string, Record<string, LckTableRow[]>>,
    /**
     * Ofline data created by the logged in user,
     * structured by group
     *
     * Data are only for POST endpoints,
     * all records need to be created.
     */
    offlineDataUser: {} as Record<
      string,
      {
        tableTranslationKey: string;
        originalData: Partial<LckTableRow>;
        formattedData?: Record<string, LckTableRowData>;
        status: SYNC_STATUS;
        action: SYNC_ACTION;
      }[]
    >,
    syncing: false,
    syncError: null as Error | null,
  });

  const { appState } = useStoreApp();

  /**
   * For a given user,
   * * store all diving sites for further search,
   * * store all groups / chapters / pages / forms / ...
   *
   * Each user's group can have different data,
   * so we need to store them separately.
   *
   */
  async function retrieveDataForOfflineUse(userId: number) {
    console.info(
      "[data store] Retrieving data for offline. May take some time...",
    );

    dataState.value.loading = true;

    if (!appState.isOnline) {
      console.error(
        "[data store] Cannot retrieve data for offline, app state is offline.",
      );
      return;
    }

    try {
      // groups > aclset > chapter
      dataState.value.groups = (await lckServices.group.find({
        query: {
          "users.id": userId,
          $joinRelation: "users",
          $eager: "[aclset.[chapter]]",
          $limit: -1,
        },
      })) as LckGroup[];

      // pages > containers > blocks
      // await Promise.all(
      await dataState.value.groups.reduce(async (previousPromise, g) => {
        await previousPromise;
        dataState.value.offlineDataDictionary[g.id] = {};
        dataState.value.offlineDataServer[g.id] = {};
        const pages = (await lckServices.page.find({
          query: {
            chapter_id: g.aclset?.chapter?.id,
            $eager: "[containers.[blocks]]",
            $limit: 20,
          },
        })) as Paginated<LckPage>;

        // all RELATION_BETWEEN_TABLES that could be searched in forms
        const tableViewIds = new Set();

        pages.data.forEach((p) => {
          p.containers.forEach((c) => {
            c.blocks.forEach((b) => {
              switch (b.type) {
                case BLOCK_TYPE.TABLE_SET:
                case BLOCK_TYPE.DATA_RECORD:
                case BLOCK_TYPE.FORM_RECORD:
                  tableViewIds.add(
                    (b as BlockFormRecord | BlockDataRecord | BlockTableSet)
                      .settings.id as string,
                  );
                  break;
              }
            });
          });
        });
        ((g.aclset as LckAclSet).chapter as LckChapter).pages = pages.data;

        // const TABLE_VIEW_ID = '42a912cc-8092-4bc4-a693-1202a98fb047'

        // table views for the current page
        const tableViews = (await lckServices.tableView.find({
          query: {
            $eager: "[columns.[column_type, parents.^]]",
            id: {
              $in: Array.from(tableViewIds),
            },
            $limit: 20,
          },
        })) as Paginated<LckTableView>;

        tableViews.data.forEach((d) =>
          d.columns?.sort((a, b) => (a.position > b.position ? 1 : 0)),
        );
        dataState.value.views[g.id] = tableViews.data;
        // find all RELATION_BETWEEN_TABLES fields and their table_ids
        const rbtTableIds = new Set<string>();
        tableViews.data.forEach((tv) => {
          tv.columns
            ?.filter(
              (tvc) =>
                tvc.column_type_id === COLUMN_TYPE.RELATION_BETWEEN_TABLES,
            )
            .forEach((tvc) => rbtTableIds.add(tvc.settings.tableId as string));

          /**
           * Enhance all single select
           * by creating a "selectOptions" property easier to manipulate for p-select component
           */
          tv.columns
            ?.filter((f) => f.column_type_id === COLUMN_TYPE.SINGLE_SELECT)
            .forEach((f) => {
              const values = f?.settings?.values;
              if (values) {
                f.selectOptions = Object.keys(values)
                  .map((key) => ({
                    value: key,
                    label: values[key].label, // TODO: use a key for translation purpose instead of the label
                    color: values[key].color,
                    backgroundColor: values[key].backgroundColor,
                    position: values[key].position || 0,
                  }))
                  .sort(
                    (firstValue, secondValue) =>
                      firstValue.position - secondValue.position,
                  );
              }
            });
        });

        // retrieve all data from these columns
        await Array.from(rbtTableIds)
          .filter((tableId) => TABLE_IDS_TO_STORE_OFFLINE.includes(tableId))
          .filter((_v, i) => i < 3)
          .reduce(
            async (previousPromise: Promise<void>, currentTableId: string) => {
              await previousPromise;
              try {
                const tableRows = (await lckServices.tableRow.find({
                  query: {
                    table_id: currentTableId,
                    $select: ["id", "text"], // this is not working...
                    $lckGroupId: g.id,
                    $sort: {
                      text: 1,
                    },
                    $limit: -1,
                  },
                })) as LckTableRow[];
                dataState.value.offlineDataDictionary[g.id][currentTableId] =
                  tableRows.map((tr) => ({
                    id: tr.id,
                    text: tr.text,
                    data: {},
                    table_id: tr.table_id,
                  }));
              } catch (error) {
                return Promise.reject(error);
              }
            },
            Promise.resolve(),
          );

        // retrieve all data from table view we need to store offline
        await dataState.value.views[g.id]
          .filter((ctv) => TABLE_VIEW_IDS_TO_STORE_OFFLINE.includes(ctv.id))
          .reduce(
            async (
              previousPromise: Promise<void>,
              currentTableView: LckTableView,
            ) => {
              await previousPromise;
              try {
                const tableViewRows = (await lckServices.tableRow.find({
                  query: {
                    table_view_id: currentTableView.id,
                    // $select: ['id', 'text'], // this is not working...
                    $lckGroupId: g.id,
                    $sort: {
                      text: 1,
                    },
                    $limit: -1,
                  },
                })) as LckTableRow[];
                dataState.value.offlineDataServer[g.id][currentTableView.id] =
                  tableViewRows.map((tr) => ({
                    id: tr.id,
                    text: tr.text,
                    data: tr.data,
                    table_id: tr.table_id,
                    table_view_id: tr.table_view_id,
                  }));
              } catch (error) {
                return Promise.reject(error);
              }
            },
            Promise.resolve(),
          );
      }, Promise.resolve());
      // )

      localStorage.setItem(
        LCK_SETTINGS.LOCALSTORAGE_KEY_USER_GROUPS,
        JSON.stringify(dataState.value.groups),
      );
      localStorage.setItem(
        LCK_SETTINGS.LOCALSTORAGE_KEY_USER_VIEWS,
        JSON.stringify(dataState.value.views),
      );
      localStorage.setItem(
        LCK_SETTINGS.LOCALSTORAGE_KEY_OFFLINE_DATA_DICTIONARY,
        JSON.stringify(dataState.value.offlineDataDictionary),
      );
      dexieStorageInstance.set(
        LCK_SETTINGS.LOCALSTORAGE_KEY_OFFLINE_DATA_SERVER,
        dataState.value.offlineDataServer,
      );
      localStorage.setItem(
        LCK_SETTINGS.LOCALSTORAGE_KEY_OFFLINE_LAST_RETRIEVAL_DATETIME,
        Date.now().toString(),
      );

      dataState.value.offlineReady = true;
    } catch (e) {
      console.error(e);
      dataState.value.error = e as Error;
    }

    dataState.value.loading = false;

    console.info("[data store] Retrieve over !");
  }

  /**
   * Restore offline dictionaries / groups / views from localStorage
   * to the dataStore
   */
  async function restoreOfflineDictionariesFromLocalStorage() {
    dataState.value.groups = JSON.parse(
      localStorage.getItem(LCK_SETTINGS.LOCALSTORAGE_KEY_USER_GROUPS) || "[]",
    );
    dataState.value.views = JSON.parse(
      localStorage.getItem(LCK_SETTINGS.LOCALSTORAGE_KEY_USER_VIEWS) || "[]",
    );
    dataState.value.offlineDataDictionary = JSON.parse(
      localStorage.getItem(
        LCK_SETTINGS.LOCALSTORAGE_KEY_OFFLINE_DATA_DICTIONARY,
      ) || "{}",
    );
    dataState.value.offlineDataServer =
      (await dexieStorageInstance.get(
        LCK_SETTINGS.LOCALSTORAGE_KEY_OFFLINE_DATA_SERVER,
      )) ?? {};
  }

  /**
   * Restore offline data user from localStorage
   * to the dataStore
   */
  function restoreOfflineDataUserFromLocalStorage() {
    dataState.value.offlineDataUser = JSON.parse(
      localStorage.getItem(LCK_SETTINGS.LOCALSTORAGE_KEY_OFFLINE_DATA_USER) ||
        "{}",
    );
  }

  /**
   * Feed data from local storage and/or CaPeL back office,
   * according to the app state :
   * * we load everything from local storage
   * * if app is online and if last time of retrieval is recent (< 12h), we keep local storage data
   */
  async function feedDataStore(userId: number) {
    console.info(
      "[data store] Feeding data for offline. May take some time...",
    );
    restoreOfflineDataUserFromLocalStorage();
    await restoreOfflineDictionariesFromLocalStorage();
    if (appState.isOnline) {
      const lastRetrievalDatetime = parseInt(
        localStorage.getItem(
          LCK_SETTINGS.LOCALSTORAGE_KEY_OFFLINE_LAST_RETRIEVAL_DATETIME,
        ) || "0",
        10,
      );
      const elapsedTime = Date.now() - lastRetrievalDatetime;
      // if (elapsedTime > 12 * 60 * 60 * 1000) {
      await retrieveDataForOfflineUse(userId);
      // } else restoreOfflineDictionariesFromLocalStorage()
    }
    dataState.value.offlineReady = true;
    console.info(
      "[data store] Feeding over. Ready to be offline (if not already !).",
    );
  }

  /**
   * Reset the data store, localStorage and pinia ones.
   */
  async function resetDataStore() {
    console.info("[data store] Reseting data...");

    localStorage.removeItem(LCK_SETTINGS.LOCALSTORAGE_KEY_USER_GROUPS);
    localStorage.removeItem(LCK_SETTINGS.LOCALSTORAGE_KEY_USER_VIEWS);
    localStorage.removeItem(
      LCK_SETTINGS.LOCALSTORAGE_KEY_OFFLINE_DATA_DICTIONARY,
    );
    await dexieStorageInstance.remove(
      LCK_SETTINGS.LOCALSTORAGE_KEY_OFFLINE_DATA_SERVER,
    );
    localStorage.removeItem(
      LCK_SETTINGS.LOCALSTORAGE_KEY_OFFLINE_LAST_RETRIEVAL_DATETIME,
    );

    dataState.value.groups = [];
    dataState.value.views = {};
    dataState.value.offlineDataDictionary = {};
    dataState.value.offlineDataUser = {};
    dataState.value.offlineDataServer = {};

    console.info("[data store] Reseting over.");
  }

  /**
   * Sync the data stored locally on the web server
   *
   * Remove each data correctly sent.
   *
   * Store eventually errors if sync fail.
   */
  async function syncData() {
    console.info("[data store] start a sync...");
    dataState.value.syncing = true;
    try {
      await Promise.all(
        Object.keys(dataState.value.offlineDataUser).map(async (groupId) => {
          const offlineDataUserGroup = dataState.value.offlineDataUser[groupId];
          await Promise.all(
            offlineDataUserGroup.map(async (currentRecord) => {
              if (currentRecord.status === SYNC_STATUS.SUCCESS) return;
              try {
                currentRecord.status = SYNC_STATUS.IN_PROGRESS;
                switch (currentRecord.action) {
                  case SYNC_ACTION.DELETE:
                    await lckServices.tableRow.remove(
                      currentRecord.originalData.id as string,
                      {
                        $lckGroupId: groupId,
                      },
                    );
                    break;
                  case SYNC_ACTION.PATCH:
                    await lckServices.tableRow.patch(
                      currentRecord.originalData.id as string,
                      {
                        data: currentRecord.formattedData,
                        table_view_id: currentRecord.originalData.table_view_id,
                        $lckGroupId: groupId,
                      },
                    );
                    break;
                  case SYNC_ACTION.POST:
                  default:
                    await lckServices.tableRow.create({
                      data: currentRecord.formattedData,
                      table_view_id: currentRecord.originalData.table_view_id,
                      $lckGroupId: groupId,
                    });
                }
                currentRecord.status = SYNC_STATUS.SUCCESS;
              } catch (error) {
                currentRecord.status = SYNC_STATUS.FAILURE;
                throw error;
              }
            }),
          );
        }),
      );
    } catch (err) {
      console.error(err);
      dataState.value.error = err as Error;
    }

    /**
     * Remove all records with SUCCESS
     */
    Object.keys(dataState.value.offlineDataUser).map(async (groupId) => {
      dataState.value.offlineDataUser[groupId] =
        dataState.value.offlineDataUser[groupId].filter(
          (d) => d.status !== SYNC_STATUS.SUCCESS,
        );
    });

    dataState.value.syncing = false;
    console.info("[data store] sync finished.");
  }

  /**
   * Add a new data to offline ones
   *
   * and trigger a sync if online
   */
  async function addData(
    record: Partial<LckTableRowOffline>,
    groupId: string,
    formFieldsDisplayed: LckTableViewColumn[],
  ) {
    console.info("[data store] adding new record to local storage...");
    if (!dataState.value.offlineDataUser[groupId])
      dataState.value.offlineDataUser[groupId] = [];

    /**
     * We need to detect if this is a new record
     * or the update of an existing record
     */
    // console.log('record offline', record, record.offline, record.offlineIndex)
    if (record.offline) {
      // we are on an offline record
      // we need to update it
      // record.offlineIndex store the index
      const currentOfflineRecord =
        dataState.value.offlineDataUser[groupId][record.offlineIndex as number];
      currentOfflineRecord.originalData = record;
      currentOfflineRecord.formattedData = formatRowData(
        record.data || {},
        formFieldsDisplayed,
      );
      currentOfflineRecord.status = SYNC_STATUS.WAITING;
    } else {
      // new record, we only push it
      dataState.value.offlineDataUser[groupId].push({
        tableTranslationKey: "capel.tables.dive",
        originalData: record,
        formattedData: formatRowData(record.data || {}, formFieldsDisplayed),
        status: SYNC_STATUS.WAITING,
        action: record.id ? SYNC_ACTION.PATCH : SYNC_ACTION.POST,
      });
    }

    if (!appState.isOnline) {
      console.info("[data store] is offline. sync new record later.");
      return {
        code: "DS_ADD_DATA_OK",
      };
    }

    console.info("[data store] is online. trigger sync for new record.");
    await syncData();
  }

  /**
   * Patch a record from offline data
   *
   * and trigger a sync if online
   */
  // async function patchData(
  // record: LckTableRow,
  // groupId: string
  // ) {
  // }

  /**
   * Remove a record from offline data
   *
   * and trigger a sync if online
   */
  async function removeData(
    record: Partial<LckTableRowOffline>,
    groupId: string,
  ) {
    console.info("[data store] removing record to local storage...");
    if (!dataState.value.offlineDataUser[groupId])
      dataState.value.offlineDataUser[groupId] = [];

    /**
     * We need to detect if this is an online record
     * or the removal of an existing record
     */
    // console.log('record offline', record, record.offline, record.offlineIndex)
    if (record.offline) {
      // we are on an offline record
      // we need to remove it
      // record.offlineIndex store the index
      dataState.value.offlineDataUser[groupId].splice(
        record.offlineIndex as number,
        1,
      );
      // we exit now as it's not necessary to sync this
      return {
        code: "DS_REMOVE_DATA_OK",
      };
    } else {
      // remove it now
      await lckServices.tableRow.remove(record.id as string, {
        $lckGroupId: groupId,
      });
    }

    if (!appState.isOnline) {
      console.info("[data store] is offline. sync removal record later.");
      return {
        code: "DS_REMOVE_DATA_OK",
      };
    }

    console.info("[data store] is online. trigger sync for removal record.");
    await syncData();
  }

  /**
   * Search any data,
   * * locally if offline & app is ready
   * * against the Backoffice API if online
   */
  async function searchData(
    groupId: string,
    tableId: string,
    query: string,
    filter: Record<string, string> = {},
  ) {
    const queryNormalized = normalizeString(query);

    if (appState.isOnline) {
      const result = (await lckServices.tableRow.find({
        query: {
           
          table_id: tableId,
          text: {
            $unaccent: `%${queryNormalized}%`,
          },
          $lckGroupId: groupId,
          $sort: {
            text: 1,
          },
          $limit: 20,
          ...filter,
        },
      })) as Paginated<LckTableRow>;
      return result.data.map((d) => ({
        label: d.text,
        value: d.id,
      }));
    } else {
      /**
       * We are offline,
       * we search through our data store
       */
      return dataState.value.offlineDataDictionary[groupId]?.[tableId]
        ?.filter(
          (value) => normalizeString(value.text).indexOf(queryNormalized) > -1,
        )
        .filter((_v, i) => i < 20)
        .map((d) => ({
          label: d.text,
          value: d.id,
        }));
    }
  }

  /**
   * Extract a comparable text from a cell value,
   * whether it's a plain value or a relation complex one ({ reference, value }).
   */
  function extractCellText(value: LckTableRowData | undefined): string {
    if (value == null) return "";
    if (typeof value === "object" && "value" in value) {
      return String((value as LckTableRowDataComplex).value ?? "");
    }
    return String(value);
  }

  /**
   * Reverse lookup against the offline snapshot of a related view.
   *
   * Search rows of `viewId` whose `searchFieldId` text-matches `query`,
   * then collect the ids referenced through their `referenceFieldId` relation.
   *
   * Used to resolve eg "ids of profiles owning a boat whose name matches".
   * The view must be stored offline (TABLE_VIEW_IDS_TO_STORE_OFFLINE).
   */
  function getReferencedIdsBySearch(
    groupId: string,
    viewId: string,
    searchFieldId: string,
    query: string,
    referenceFieldId: string,
  ): string[] {
    const normalizedQuery = normalizeString(query);
    const rows = dataState.value.offlineDataServer[groupId]?.[viewId] ?? [];
    const ids = new Set<string>();
    rows.forEach((row) => {
      const text = extractCellText(row.data?.[searchFieldId]);
      if (normalizeString(text).indexOf(normalizedQuery) > -1) {
        const reference = (
          row.data?.[referenceFieldId] as LckTableRowDataComplex | undefined
        )?.reference;
        if (reference) ids.add(reference);
      }
    });
    return Array.from(ids);
  }

  /**
   * Return the rows of the offline snapshot of `viewId`
   * whose `referenceFieldId` relation points to `referenceId`.
   *
   * Used to display eg "the boats of the selected profile".
   * The view must be stored offline (TABLE_VIEW_IDS_TO_STORE_OFFLINE).
   */
  function getSnapshotRowsByReference(
    groupId: string,
    viewId: string,
    referenceFieldId: string,
    referenceId: string,
  ): LckTableRow[] {
    const rows = dataState.value.offlineDataServer[groupId]?.[viewId] ?? [];
    return rows.filter(
      (row) =>
        (row.data?.[referenceFieldId] as LckTableRowDataComplex | undefined)
          ?.reference === referenceId,
    );
  }

  /**
   * Update local storage when needed
   */
  watch(
    () => dataState.value.offlineDataUser,
    (newUserData) => {
      console.debug(
        "[data store] new offline data user to store",
        newUserData,
        LCK_SETTINGS.LOCALSTORAGE_KEY_OFFLINE_DATA_USER,
      );
      localStorage.setItem(
        LCK_SETTINGS.LOCALSTORAGE_KEY_OFFLINE_DATA_USER,
        JSON.stringify(newUserData),
      );
    },
    { deep: true },
  );

  /**
   * Sync data when app is online again
   */
  watch(
    () => appState.isOnline,
    (isOnline) => {
      if (isOnline === true) {
        syncData();
      }
    },
  );

  return {
    dataState,
    feedDataStore,
    resetDataStore,
    syncData,
    addData,
    searchData,
    removeData,
    getReferencedIdsBySearch,
    getSnapshotRowsByReference,
  };
});
